import dayjs from 'dayjs';
import { observer } from 'mobx-react-lite';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import DateRangePicker, {
  DateRangePickerDateSelectFunction,
  NullableDate,
} from '~components/Filters/DateRangePicker';
import { SearchParams } from '~constants/enums';
import { useStores } from '~store';
import {
  persistDateRange,
  retrievePersistedDateRange,
} from '~utils/dateRangePersistence';

import { driverPayDateRangeStorageKey } from '../DriverPayV2/utils';

export const DriverDayDateFilter = observer(function () {
  const { settlementsStore } = useStores();
  const { datesDroppedByAutoExtend } = settlementsStore;
  const [searchParams, setSearchParams] = useSearchParams();
  const initialStartDate = searchParams.get(SearchParams.START_DATE)
    ? dayjs(searchParams.get(SearchParams.START_DATE)).tz()
    : null;

  const initialEndDate = searchParams.get(SearchParams.END_DATE)
    ? dayjs(searchParams.get(SearchParams.END_DATE)).tz()
    : null;

  const [startDate, setStartDate] = useState<NullableDate>(initialStartDate);
  const [endDate, setEndDate] = useState<NullableDate>(initialEndDate);

  // Persist only ranges the user actually picked: the close handler below also
  // fires when the popover is opened and dismissed without a selection.
  const userSelectedDatesRef = useRef(false);

  // A fully paramless URL usually means internal navigation dropped
  // the date params (the Payables/Receivables nav links carry none). Re-inject
  // this category's persisted range into the URL — which DriverPaySettlements
  // derives its query from — and into the picker state directly (clean stored
  // dates). Stored explicit nulls (the user cleared) or an empty
  // key inject nothing and reset the picker instead, so an externally cleared
  // range (grid clear-filters hint, category nav) can't keep displaying dates
  // the query no longer sends; partial deep links are left untouched. Dates an
  // auto-extended search dropped stay off the URL until the user picks or
  // restores a range, or leaves Settlements.
  useEffect(() => {
    // A range the user is still picking wins until the popover closes.
    if (userSelectedDatesRef.current) {
      return;
    }

    const urlStartDate = searchParams.get(SearchParams.START_DATE);
    const urlEndDate = searchParams.get(SearchParams.END_DATE);

    if (urlStartDate || urlEndDate) {
      setStartDate(urlStartDate ? dayjs(urlStartDate).tz() : null);
      setEndDate(urlEndDate ? dayjs(urlEndDate).tz() : null);

      return;
    }

    if (datesDroppedByAutoExtend) {
      setStartDate(null);
      setEndDate(null);

      return;
    }

    const storedDateRange = retrievePersistedDateRange(
      driverPayDateRangeStorageKey(searchParams.get(SearchParams.INVOICE_CATEGORY)),
    );
    const restoredStartDate = storedDateRange?.startDate
      ? dayjs.tz(storedDateRange.startDate)
      : null;
    const restoredEndDate = storedDateRange?.endDate
      ? dayjs.tz(storedDateRange.endDate).endOf('day')
      : null;

    if (!restoredStartDate && !restoredEndDate) {
      setStartDate(null);
      setEndDate(null);

      return;
    }

    setStartDate(restoredStartDate);
    setEndDate(restoredEndDate);

    setSearchParams(
      (params) => {
        if (restoredStartDate) {
          params.set(
            SearchParams.START_DATE,
            restoredStartDate.startOf('day').toISOString(),
          );
        }
        if (restoredEndDate) {
          params.set(SearchParams.END_DATE, restoredEndDate.toISOString());
        }
        return params;
      },
      { replace: true },
    );
  }, [searchParams, datesDroppedByAutoExtend, setSearchParams]);

  const handleDateChange = useCallback<DateRangePickerDateSelectFunction>(
    (start, end) => {
      userSelectedDatesRef.current = true;
      setStartDate(start);
      // we get a null end date when the user selects a single date but we want to have both start and end dates
      // so we set the end date to the start date's end of day
      setEndDate(end ?? start?.endOf('day') ?? null);
    },
    [],
  );

  const onFilterClosed = useCallback(() => {
    if (userSelectedDatesRef.current) {
      settlementsStore.markDateScopeUserChosen();
      // Explicit nulls mean the user cleared to "All dates" — returning to the
      // tab must not re-hydrate the previous range.
      persistDateRange(
        driverPayDateRangeStorageKey(searchParams.get(SearchParams.INVOICE_CATEGORY)),
        {
          endDate: endDate ? endDate.format('YYYY-MM-DD') : null,
          startDate: startDate ? startDate.format('YYYY-MM-DD') : null,
        },
      );
    }
    userSelectedDatesRef.current = false;

    setSearchParams((params) => {
      if (startDate) {
        params.set(
          SearchParams.START_DATE,
          dayjs.tz(startDate).startOf('day').toISOString(),
        );
      } else {
        params.delete(SearchParams.START_DATE);
      }
      if (endDate) {
        params.set(SearchParams.END_DATE, dayjs.tz(endDate).endOf('day').toISOString());
      } else {
        params.delete(SearchParams.END_DATE);
      }
      return params;
    });
  }, [setSearchParams, startDate, endDate, searchParams]);

  return (
    <DateRangePicker
      allowAllDates
      endDate={endDate}
      mode="icon"
      onAnchorClose={onFilterClosed}
      onDateSelect={handleDateChange}
      showShortcuts
      startDate={startDate}
      useTodayFormat
    />
  );
});
